import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { InstructionWrapper, InstructionsContainer, InstructionsHeader, TextDescriptionContainer, EnhanceWrapper} from './InstructionsPage.styled'
import { useStore } from '../../../../../hooks'
import { observer } from 'mobx-react'
import { Typography, TextDescription, Button, Alert } from '@ann/web-components'
import  { FloatingCard } from './components/FloatingCard'

const InstructionsPage = observer(() => {
    const { instructionsStore, playgroundConfigStore } = useStore();
    const { agentId } = useParams();
    const [hasTextSelected, setHasTextSelected] = useState(false);
    const [isFloatingCardOpen, setIsFloatingCardOpen] = useState(false);
    const [selectedText, setSelectedText] = useState('');
    const [floatingCardText, setFloatingCardText] = useState(''); // Text to display in floating card
    const textDescriptionRef = useRef(null);


    // Set playgroundConfigStore reference
    useEffect(() => {
        instructionsStore.setPlaygroundConfigStore(playgroundConfigStore);
    }, [instructionsStore, playgroundConfigStore]);

    // Load agent instruction when component mounts or agentId changes
    useEffect(() => {
        if (agentId) {
            instructionsStore.fetchAgentInstruction(agentId).then(() => {
                // Decide which mode to use based on whether it has instruction
                if (instructionsStore.hasExistingInstruction) {
                    // Agent ALREADY has instruction → UPDATE mode
                    instructionsStore.startUpdateMode();
                }
            });
        }
    }, [agentId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            instructionsStore.stopAutoSave();
        };
    }, [instructionsStore]);

    // Add event listeners for text selection
    useEffect(() => {
        const handleSelectionChange = () => {
            const selection = window.getSelection();
            const selectedText = selection.toString().trim();
            setHasTextSelected(selectedText.length > 0);
            setSelectedText(selectedText);
        };

        const handleMouseUp = () => {
            // Small delay to ensure selection is updated
            setTimeout(handleSelectionChange, 10);
        };

        const handleKeyUp = (e) => {
            // Check selection on arrow keys, shift+arrow, etc.
            if (e.key.includes('Arrow') || e.key === 'Shift') {
                setTimeout(handleSelectionChange, 10);
            }
        };

        // Add event listeners to document
        document.addEventListener('selectionchange', handleSelectionChange);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const handleInstructionChange = (value) => {
        instructionsStore.setCurrentInstruction({
            instruction: value
        });
        
        // Notify the store that the user is typing
        instructionsStore.handleUserTyping();
    };

    const handleEnhanceClick = () => {
        // Capture the currently selected text and save it to floating card
        const selection = window.getSelection();
        const currentSelectedText = selection.toString().trim();
        setSelectedText(currentSelectedText);
        setFloatingCardText(currentSelectedText); // Save text for floating card
        setIsFloatingCardOpen(true);
    };

    const handleCloseFloatingCard = () => {
        setIsFloatingCardOpen(false);
        setFloatingCardText(''); // Clear floating card text when closing
    };

    return (
        <>
            <InstructionsContainer>
                <InstructionWrapper>
                    <InstructionsHeader>
                        <Typography category="subtitle" size="large" weight="bold">
                            Builder
                        </Typography>

                        <Typography category="body" size="medium" weight="regular" color="body">
                            Modify instructions anytime change them or delete them as needed.
                        </Typography>
                    </InstructionsHeader>

                    <TextDescriptionContainer>
                        <TextDescription
                            expandable
                            height="172px"
                            label="Write Your Instruction"
                            value={instructionsStore.currentInstruction.instruction}
                            onChange={handleInstructionChange}
                            placeholder="What will this agent do? How will they behave? What should they avoid doing?"
                            size="medium"
                        />
                    </TextDescriptionContainer>
                </InstructionWrapper>
                {hasTextSelected && !isFloatingCardOpen && (
                    <EnhanceWrapper>
                        <Button
                            iconLeft="aiChat"
                            label="Enhance"
                            pill
                            size="small"
                            type="primary"
                            onClick={handleEnhanceClick}
                        />
                    </EnhanceWrapper>
                )}
                
                <FloatingCard 
                    isOpen={isFloatingCardOpen}
                    onClose={handleCloseFloatingCard}
                    selectedText={floatingCardText}
                />
            </InstructionsContainer>

            {/* Alert para notificaciones */}
            {instructionsStore.showAlert && (
                <Alert
                    title=""
                    description={instructionsStore.alertMessage}
                    severity={instructionsStore.alertType}
                    maxWidth="500px"
                    closable={true}
                    autoClose={true}
                    autoCloseDelay={5000}
                    onClose={() => instructionsStore.hideNotification()}
                    zIndex={1300}
                />
            )}
        </>
    );
})

export default InstructionsPage
